import React, { useState, useEffect } from 'react';
import { alpha } from '@mui/material/styles';
import { THEME_COLORS } from '../shared/constants/theme';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tabs,
  Tab,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { detectThemeSync, watchThemeChanges } from '../utils/themeUtils';
import { formatHotelDate } from '../utils/dateUtils';
import {
  getConfigurationActionHeaderRowSx,
  getConfigurationAddButtonSx,
  getConfigurationEmbeddedRootSx,
  isConfigurationMobileLayout,
} from '../utils/configurationMobileLayout';
import { usePolicies } from '../hooks/usePolicies';
import { useSnackbar } from '../hooks/useSnackbar';
import { PolicyDrawer } from '../components/policies/PolicyDrawer';
import { PolicyViewDialog } from '../components/policies/PolicyViewDialog';
import {
  createPolicy,
  updatePolicy,
  deletePolicy,
} from '../services/policyService';
import { THEME_COLOR } from '../constants/policies';

const TermsAndPolicyPage = ({ showHeader = true }) => {
  const isMobileLayout = isConfigurationMobileLayout();
  const [currentTheme, setCurrentTheme] = useState(() => detectThemeSync());
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [policyToDelete, setPolicyToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [policyToView, setPolicyToView] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  const { policies, loading, fetchPolicies } = usePolicies();
  const { snackbar, showSuccess, showError, hideSnackbar } = useSnackbar();

  useEffect(() => {
    const cleanup = watchThemeChanges((newTheme) => {
      setCurrentTheme(newTheme);
    });
    return cleanup;
  }, []);

  useEffect(() => {
    fetchPolicies().catch(() => {
      showError('Failed to load policies. Please try again.');
    });
  }, [fetchPolicies, showError]);

  const isDark = currentTheme === 'dark';

  const handleOpenDrawer = (policy = null) => {
    setSelectedPolicy(policy);
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setSelectedPolicy(null);
  };

  const handleSave = async (formData) => {
    setSaving(true);
    try {
      if (selectedPolicy) {
        // Update existing policy
        await updatePolicy(selectedPolicy.id, formData);
        showSuccess('Policy updated successfully');
      } else {
        // Create new policy
        await createPolicy(formData);
        showSuccess('Policy created successfully');
      }
      await fetchPolicies();
      handleCloseDrawer();
    } catch (error) {showError(error.response?.data?.error || 'Failed to save policy. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (policy) => {
    setPolicyToDelete(policy);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!policyToDelete) return;

    setDeleting(true);
    try {
      await deletePolicy(policyToDelete.id);
      showSuccess('Policy deleted successfully');
      await fetchPolicies();
      setDeleteDialogOpen(false);
      setPolicyToDelete(null);
    } catch (error) {showError(error.response?.data?.error || 'Failed to delete policy. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleActive = async (policy) => {
    try {
      await updatePolicy(policy.id, {
        ...policy,
        is_active: !policy.is_active,
      });
      showSuccess(`Policy ${!policy.is_active ? 'activated' : 'deactivated'} successfully`);
      await fetchPolicies();
    } catch (error) {showError('Failed to update policy status. Please try again.');
    }
  };

  const handleViewClick = (policy) => {
    setPolicyToView(policy);
    setViewDialogOpen(true);
  };

  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    setPolicyToView(null);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return formatHotelDate(dateString) || dateString;
  };

  const getPolicyTypeLabel = (type) => {
    const typeMap = {
      terms_and_conditions: 'Terms and Conditions',
      privacy_policy: 'Privacy Policy',
      child_policy: 'Child Policy',
      cancellation_policy: 'Cancellation Policy',
      refund_policy: 'Refund Policy',
      check_in_policy: 'Check-in Policy',
      check_out_policy: 'Check-out Policy',
      pet_policy: 'Pet Policy',
      smoking_policy: 'Smoking Policy',
      age_restriction_policy: 'Age Restriction Policy',
    };
    return typeMap[type] || type;
  };

  return (
    <Box sx={{ p: showHeader ? 3 : 0, ...getConfigurationEmbeddedRootSx(isMobileLayout) }}>
      {/* Header */}
      {showHeader && (
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 1 }}>
              Terms & Policies
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage hotel policies and terms
            </Typography>
          </Box>
        </Box>
      )}
      
      <Box sx={getConfigurationActionHeaderRowSx(isMobileLayout)}>
        <Box sx={{ display: { xs: 'none', sm: 'block' } }} />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDrawer()}
          sx={getConfigurationAddButtonSx(THEME_COLOR, isMobileLayout)}
        >
          Add Policy
        </Button>
      </Box>

      {/* Policies Tabs */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
        </Box>
      ) : policies.length === 0 ? (
        <Box
          sx={{
            textAlign: 'center',
            py: 8,
            px: 3,
            backgroundColor: isDark ? '#2a2a2a' : '#f5f5f5',
            borderRadius: 2,
          }}
        >
          <Typography variant="h6" sx={{ mb: 1, color: 'text.secondary' }}>
            No policies found
          </Typography>
          <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
            Get started by adding your first policy
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDrawer()}
            sx={{
              backgroundColor: THEME_COLOR,
              color: '#000',
              fontWeight: 600,
              textTransform: 'none',
              '& .MuiButton-startIcon': { color: '#000' },
              '&:hover': {
                backgroundColor: '#3ab89a',
                color: '#000',
                '& .MuiButton-startIcon': { color: '#000' },
              },
            }}
          >
            Add Policy
          </Button>
        </Box>
      ) : (
        <Paper
          elevation={0}
          sx={{
            backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
            border: `1px solid ${isDark ? alpha('#ffffff', 0.08) : '#e0e0e0'}`,
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          {/* Tabs Header */}
          <Box
            sx={{
              borderBottom: 1,
              borderColor: isDark ? alpha('#ffffff', 0.08) : '#e0e0e0',
              backgroundColor: isDark ? '#1a1a1f' : '#f5f5f5',
            }}
          >
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  minHeight: 64,
                  color: isDark ? '#999' : '#757575',
                  '&.Mui-selected': {
                    color: THEME_COLOR,
                  },
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: THEME_COLOR,
                  height: 3,
                },
              }}
            >
              {policies.map((policy, index) => (
                <Tab
                  key={policy.id}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {policy.title}
                      </Typography>
                      {policy.is_active ? (
                        <CheckCircleIcon sx={{ fontSize: 16, color: '#4caf50' }} />
                      ) : (
                        <CancelIcon sx={{ fontSize: 16, color: '#999' }} />
                      )}
                    </Box>
                  }
                />
              ))}
            </Tabs>
          </Box>

          {/* Tab Content */}
          {policies[activeTab] && (
            <Box sx={{ p: { xs: 1.5, sm: 4 } }}>
              <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'stretch', sm: 'flex-start' },
                gap: { xs: 1.5, sm: 0 },
                mb: 3,
              }}>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      mb: 1,
                      color: isDark ? '#fff' : '#212121',
                    }}
                  >
                    {policies[activeTab].title}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                    <Chip
                      label={getPolicyTypeLabel(policies[activeTab].policy_type)}
                      size="small"
                      sx={{
                        backgroundColor: isDark ? 'rgba(76, 225, 182, 0.15)' : 'rgba(76, 225, 182, 0.1)',
                        color: THEME_COLOR,
                        fontWeight: 600,
                      }}
                    />
                    <Chip
                      label={policies[activeTab].is_active ? 'Active' : 'Inactive'}
                      size="small"
                      color={policies[activeTab].is_active ? 'success' : 'default'}
                      icon={policies[activeTab].is_active ? <CheckCircleIcon /> : <CancelIcon />}
                    />
                    {policies[activeTab].version && (
                      <Chip
                        label={`Version ${policies[activeTab].version}`}
                        size="small"
                        variant="outlined"
                      />
                    )}
                    {policies[activeTab].requires_acknowledgment && (
                      <Chip
                        label="Requires Acknowledgment"
                        size="small"
                        color="warning"
                      />
                    )}
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
                  <Tooltip title={policies[activeTab].is_active ? 'Deactivate' : 'Activate'}>
                    <IconButton
                      onClick={() => handleToggleActive(policies[activeTab])}
                      sx={{
                        color: policies[activeTab].is_active ? '#4caf50' : '#999',
                        '&:hover': {
                          backgroundColor: isDark ? 'rgba(76, 225, 182, 0.1)' : 'rgba(76, 225, 182, 0.05)',
                        },
                      }}
                    >
                      {policies[activeTab].is_active ? <VisibilityIcon /> : <VisibilityOffIcon />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="View">
                    <IconButton
                      onClick={() => handleViewClick(policies[activeTab])}
                      sx={{
                        color: THEME_COLOR,
                        '&:hover': {
                          backgroundColor: isDark ? 'rgba(76, 225, 182, 0.1)' : 'rgba(76, 225, 182, 0.05)',
                        },
                      }}
                    >
                      <VisibilityIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Edit">
                    <IconButton
                      onClick={() => handleOpenDrawer(policies[activeTab])}
                      sx={{
                        color: THEME_COLOR,
                        '&:hover': {
                          backgroundColor: isDark ? 'rgba(76, 225, 182, 0.1)' : 'rgba(76, 225, 182, 0.05)',
                        },
                      }}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton
                      onClick={() => handleDeleteClick(policies[activeTab])}
                      sx={{
                        color: '#f44336',
                        '&:hover': {
                          backgroundColor: isDark ? 'rgba(244, 67, 54, 0.1)' : 'rgba(244, 67, 54, 0.05)',
                        },
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              <Divider sx={{ my: 3, borderColor: isDark ? alpha('#ffffff', 0.08) : '#e0e0e0' }} />

              {/* Summary */}
              {policies[activeTab].summary && (
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      mb: 1,
                      color: isDark ? '#ddd' : '#666',
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      fontSize: '0.75rem',
                    }}
                  >
                    Summary
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: isDark ? '#ddd' : '#666',
                      lineHeight: 1.8,
                    }}
                  >
                    {policies[activeTab].summary}
                  </Typography>
                </Box>
              )}

              {/* Content */}
              {policies[activeTab].content && (
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      color: isDark ? '#ddd' : '#666',
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      fontSize: '0.75rem',
                    }}
                  >
                    Content
                  </Typography>
                  <Box
                    sx={{
                      p: 3,
                      backgroundColor: isDark ? '#1a1a1f' : '#f9f9f9',
                      borderRadius: 2,
                      border: `1px solid ${isDark ? alpha('#ffffff', 0.08) : '#e0e0e0'}`,
                      color: isDark ? '#ddd' : '#333',
                      '& p': { margin: '1em 0', lineHeight: 1.8 },
                      '& ul, & ol': { margin: '1em 0', paddingLeft: '2em' },
                      '& strong': { fontWeight: 600, color: isDark ? '#fff' : '#212121' },
                      '& em': { fontStyle: 'italic' },
                      '& blockquote': {
                        borderLeft: `4px solid ${THEME_COLOR}`,
                        paddingLeft: 2,
                        margin: '1em 0',
                        fontStyle: 'italic',
                        color: isDark ? '#999' : '#666',
                      },
                    }}
                    dangerouslySetInnerHTML={{ __html: policies[activeTab].content }}
                  />
                </Box>
              )}

              {/* Metadata */}
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 2,
                  mt: 3,
                  pt: 3,
                  borderTop: `1px solid ${isDark ? alpha('#ffffff', 0.08) : '#e0e0e0'}`,
                }}
              >
                {policies[activeTab].effective_date && (
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        color: isDark ? '#999' : '#757575',
                        mb: 0.5,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        fontSize: '0.7rem',
                      }}
                    >
                      Effective Date
                    </Typography>
                    <Typography variant="body2" sx={{ color: isDark ? '#ddd' : '#333', fontWeight: 600 }}>
                      {formatDate(policies[activeTab].effective_date)}
                    </Typography>
                  </Box>
                )}
                {policies[activeTab].expiry_date && (
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        color: isDark ? '#999' : '#757575',
                        mb: 0.5,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        fontSize: '0.7rem',
                      }}
                    >
                      Expiry Date
                    </Typography>
                    <Typography variant="body2" sx={{ color: isDark ? '#ddd' : '#333', fontWeight: 600 }}>
                      {formatDate(policies[activeTab].expiry_date)}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </Paper>
      )}

      {/* Policy Drawer */}
      <PolicyDrawer
        open={drawerOpen}
        policy={selectedPolicy}
        isDark={isDark}
        saving={saving}
        onClose={handleCloseDrawer}
        onSave={handleSave}
      />

      {/* View Policy Dialog */}
      <PolicyViewDialog
        open={viewDialogOpen}
        policy={policyToView}
        isDark={isDark}
        onClose={handleCloseViewDialog}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{
          sx: {
            backgroundColor: isDark ? '#2a2a2a' : '#ffffff',
          },
        }}
      >
        <DialogTitle>Delete Policy</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{policyToDelete?.title}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleting}
          >
            {deleting ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={hideSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={hideSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TermsAndPolicyPage;
